"use client";

import {
  useStartVenueStripeOnboarding,
  useVenueStripeAccount,
} from "@punchup/data/hooks";
import { PunchupButton } from "@punchup/ui-common/punchup/primitives/buttons/PunchupButton";
import { PunchupTag } from "@punchup/ui-common/punchup/primitives/display/PunchupTag";
import { VenueDashboardEvents, trackEvent } from "@punchup/analytics";
import { useState } from "react";

// Hex, not a token: PunchupTag mixes `background` through color-mix() and
// needs a concrete value (same as VenueFleetCells).
const CONNECTED_GREEN = "#16A34A";
const ACTION_AMBER = "#D97706";

interface Props {
  venuePageId: string;
}

/**
 * Settings → Payments: connect the venue's Stripe account, which ticket
 * payments are processed through and payouts are sent from. The connected
 * account is created and owned by ticketing-api; this section only shows its
 * status and sends the manager to Stripe's hosted onboarding, which returns
 * them to this page.
 */
export default function VenueStripeConnectSettings({ venuePageId }: Props) {
  const { data, isLoading, isError, isFetching, refetch } =
    useVenueStripeAccount(venuePageId);
  const startOnboarding = useStartVenueStripeOnboarding(venuePageId);
  const [redirecting, setRedirecting] = useState(false);

  if (isLoading) {
    return (
      <p className="text-sm text-secondary-blue">
        Checking your Stripe account…
      </p>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-secondary-blue">
          We couldn&apos;t load your Stripe account status. If your venue sells
          tickets through Punchup, try again in a moment.
        </p>
        <div>
          <PunchupButton
            type="button"
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? "Checking…" : "Try again"}
          </PunchupButton>
        </div>
      </div>
    );
  }

  const started = !!data.stripeAccountId;
  const busy = startOnboarding.isPending || redirecting;

  const handleConnect = () => {
    trackEvent(VenueDashboardEvents.CLICKED_CONNECT_STRIPE, {
      venuePageId,
      alreadyStarted: started,
    });
    // Come back to this page without a stale ?stripe= marker.
    const returnUrl = `${window.location.origin}${window.location.pathname}`;
    startOnboarding.mutate(returnUrl, {
      onSuccess: (link) => {
        setRedirecting(true);
        window.location.assign(link.onboardingUrl);
      },
    });
  };

  let statusTag: { label: string; background: string } | null = null;
  let copy: string;
  let buttonLabel: string;
  if (data.connected) {
    statusTag = { label: "Connected", background: CONNECTED_GREEN };
    copy =
      "Your Stripe account is connected. Ticket payments are processed through it and payouts go to its bank account.";
    buttonLabel = "Review in Stripe";
  } else if (started) {
    statusTag = { label: "Action needed", background: ACTION_AMBER };
    copy = !data.detailsSubmitted
      ? "You started connecting your Stripe account but haven't finished. Continue in Stripe to accept payments and receive payouts."
      : "Stripe needs a bit more information before you can accept payments and receive payouts. Continue in Stripe to finish.";
    buttonLabel = "Continue setup";
  } else {
    copy =
      "Connect a Stripe account to accept ticket payments online and receive payouts. You'll be taken to Stripe to finish, then brought back here.";
    buttonLabel = "Connect Stripe account";
  }

  return (
    <div className="flex flex-col gap-3">
      {statusTag && (
        <div>
          <PunchupTag
            label={statusTag.label}
            background={statusTag.background}
            size="sm"
          />
        </div>
      )}
      <p className="text-sm text-secondary-blue">{copy}</p>
      {startOnboarding.isError && (
        <p className="text-sm text-red-600" role="alert">
          We couldn&apos;t open Stripe. Please try again.
        </p>
      )}
      <div>
        <PunchupButton
          type="button"
          variant={data.connected ? "outline" : "primary"}
          onClick={handleConnect}
          disabled={busy}
        >
          {busy ? "Opening Stripe…" : buttonLabel}
        </PunchupButton>
      </div>
    </div>
  );
}
